import { useTranslation } from '../../i18n/useTranslation';
import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { pointLabel, type CoralAssessment, type LightCoral, type LightPoint, type PlacementStatus } from '../../services/lightSurveyService';
import './Lighting.css';

const STATUS_TONE: Readonly<Record<PlacementStatus, BadgeTone>> = {
  ok: 'success',
  too_low: 'warning',
  too_high: 'warning',
  uncertain: 'info',
  unknown_group: 'neutral',
  not_applicable: 'neutral',
  no_data: 'neutral',
};

export function coralName(coral: LightCoral): string {
  return coral.livestockName ?? coral.label ?? '';
}

export interface CoralPlacementListProps {
  corals: readonly LightCoral[];
  assessments: readonly CoralAssessment[];
  points: readonly LightPoint[];
  busy: boolean;
  onEdit: (coral: LightCoral) => void;
  onRemove: (coral: LightCoral) => void;
}

/**
 * The accessible result of the placement check: per coral its number on the
 * photo, care group, estimated PAR against the group's range, the verdict as
 * text (never colour only), how sure it is, the daily light integral and — when
 * it is off — the nearest measured spot that fits.
 */
export function CoralPlacementList({ corals, assessments, points, busy, onEdit, onRemove }: CoralPlacementListProps) {
  const { t } = useTranslation();
  const byId = new Map(assessments.map((a) => [a.coralId, a]));
  const sequenceOf = new Map(points.map((p) => [p.id, p.sequence]));
  return (
    <ol className="lighting__list" data-testid="coral-placements">
      {corals.map((coral, index) => {
        const a = byId.get(coral.id);
        const name = coralName(coral);
        const status = a?.status ?? 'no_data';
        const suggestionPoint = a?.suggestion ? sequenceOf.get(a.suggestion.pointId) : undefined;
        return (
          <li key={coral.id} className="lighting__row" data-testid={`coral-${coral.id}`}>
            <Stack gap="1" className="lighting__row-text">
              <Inline gap="2" align="center">
                <Text weight="semibold">{t('lighting.corals.itemTitle', { number: index + 1, name })}</Text>
                <Badge tone={STATUS_TONE[status]}>{t(`lighting.status.${status}`)}</Badge>
              </Inline>
              <Text color="secondary" size="sm">
                {coral.group ? t(`lighting.groups.${coral.group}`) : t('lighting.groups.none')}
                {a && a.minPar !== null && a.maxPar !== null ? ` ·${String(t('lighting.corals.range', { min: a.minPar, max: a.maxPar }))}` : ''}
              </Text>
              {a && a.estimatedPar !== null && (
                <Text size="sm">
                  {t('lighting.corals.estimate', { par: a.estimatedPar })}
                  {a.confidence && ` · ${String(t(`lighting.confidence.${a.confidence}`))}`}
                  {a.dli !== null && ` · ${String(t('lighting.corals.dli', { dli: a.dli }))}`}
                </Text>
              )}
              {a?.suggestion && suggestionPoint !== undefined && (
                <Text size="sm">
                  {t(`lighting.corals.suggestion.${a.suggestion.direction}`, { point: pointLabel(suggestionPoint), par: a.suggestion.par })}
                </Text>
              )}
              {(status === 'too_low' || status === 'too_high') && !a?.suggestion && <Text size="sm">{t('lighting.corals.noSpot')}</Text>}
              {status === 'uncertain' && <Text size="sm">{t('lighting.corals.measureHere')}</Text>}
              {status === 'unknown_group' && <Text size="sm">{t('lighting.corals.chooseGroup')}</Text>}
            </Stack>
            <Inline gap="2">
              <Button size="sm" variant="secondary" onClick={() => onEdit(coral)} disabled={busy} aria-label={t('lighting.corals.editLabel', { name })}>
                {t('lighting.edit')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onRemove(coral)} disabled={busy} aria-label={t('lighting.corals.removeLabel', { name })}>
                {t('lighting.remove')}
              </Button>
            </Inline>
          </li>
        );
      })}
    </ol>
  );
}

export default CoralPlacementList;
